import {
  Close as CloseIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Popover,
  styled,
  Typography,
} from '@mui/material';
import {
  ComponentType,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import { BaseDropdownTrigger, EDropdownPlacement } from '#entities';
import { routes, type ControlsProps } from '#shared';

import type { SettingsConfig } from '#features';

const PREFIX = 'TMDX-WidgetSettings';
const widgetSettingsClasses = {
  root: `${PREFIX}-root`,
  icon: `${PREFIX}-icon`,
  popover: `${PREFIX}-popover`,
  header: `${PREFIX}-header`,
  body: `${PREFIX}-body`,
  actions: `${PREFIX}-actions`,
  item: `${PREFIX}-item`,
  pageLink: `${PREFIX}-pageLink`,
  button: `${PREFIX}-button`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  [`.${widgetSettingsClasses.icon}`]: {
    display: 'block',
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  [`&.${widgetSettingsClasses.popover}`]: {
    zIndex: theme.zIndex.modal,
  },
  // Style the Paper that Popover renders internally
  '& .MuiPaper-root': {
    minWidth: 350,
    maxWidth: 500,
    width: 'auto',
    maxHeight: '80vh',
    overflow: 'auto',
    outline: 'none',
    boxShadow: theme.shadows[8],
  },
  [`.${widgetSettingsClasses.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`.${widgetSettingsClasses.body}`]: {
    padding: theme.spacing(0, 2, 2, 2),
  },
  [`.${widgetSettingsClasses.actions}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  [`.${widgetSettingsClasses.item}`]: {
    marginBottom: theme.spacing(2),
    '&:last-child': { marginBottom: 0 },
  },
  [`.${widgetSettingsClasses.pageLink}`]: {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    marginBottom: theme.spacing(1),

    '&:hover': {
      backgroundColor: theme.palette.mapBlue[800],
    },
  },

  [`& .${widgetSettingsClasses.button}`]: {
    backgroundColor: theme.palette.mapBlue[700],
    color: theme.palette.common.white,

    '& svg': {
      fill: theme.palette.common.white,
    },

    '&:hover': {
      backgroundColor: theme.palette.mapBlue[800],
    },

    '&.Mui-disabled': {
      opacity: 0.3,
      color: theme.palette.common.white,
    },
  },
}));

interface WidgetSettingsProps {
  title?: string;
  settings: SettingsConfig;
  currentSettings?: Partial<Record<string, ControlsProps['value']>>;
  onSettingChange: (name: string, value: ControlsProps['value']) => void;
  settingControlComponent: ComponentType<ControlsProps>;
  isIdle?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WidgetSettingsPopover = memo<WidgetSettingsProps>(
  ({
    title = 'Widget Settings',
    settings,
    currentSettings,
    onSettingChange,
    settingControlComponent: SettingControl,
    isIdle,
    onOpenChange,
  }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement | null>(null);

    const setOpenSafe = useCallback(
      (val: boolean) => {
        setOpen(val);
        onOpenChange?.(val);
      },
      [onOpenChange],
    );

    const handleOpen = useCallback(() => setOpenSafe(true), [setOpenSafe]);
    const handleClose = useCallback(() => setOpenSafe(false), [setOpenSafe]);

    useEffect(() => {
      if (isIdle) {
        setOpenSafe(false);
      }
    }, [isIdle, setOpenSafe]);

    const hasConfig =
      !!settings?.config && Object.keys(settings.config).length > 0;

    return (
      <Root className={widgetSettingsClasses.root}>
        <BaseDropdownTrigger
          ref={anchorRef}
          onClick={handleOpen}
          placement={EDropdownPlacement.Inline}
          className={widgetSettingsClasses.button}
        >
          <SettingsIcon className={widgetSettingsClasses.icon} />
        </BaseDropdownTrigger>

        <StyledPopover
          open={open}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          className={widgetSettingsClasses.popover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disablePortal={false}
          keepMounted
        >
          <Box>
            <Box className={widgetSettingsClasses.header}>
              <Typography variant="h6">{title}</Typography>
              <IconButton onClick={handleClose} size="small" aria-label="Close">
                <CloseIcon sx={{ color: '#fff' }} />
              </IconButton>
            </Box>

            <Box className={widgetSettingsClasses.body}>
              <Link
                to={routes.SETTINGS}
                className={widgetSettingsClasses.pageLink}
              >
                <SettingsIcon fontSize="small" sx={{ color: '#fff' }} />
                <Typography>Settings Page</Typography>
              </Link>

              {hasConfig ? (
                <>
                  {/* Render each setting control */}
                  {Object.entries(settings.config).map(([name, cfg]) => {
                    const cs = currentSettings ?? {};
                    const value = cs[name] ?? cfg.defaultValue;

                    return (
                      <Box key={name} className={widgetSettingsClasses.item}>
                        <SettingControl
                          {...(cfg as ControlsProps)}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          value={value as any}
                          onChange={(v: ControlsProps['value']) => {
                            (
                              cfg.onChange as
                                | ((v: ControlsProps['value']) => void)
                                | undefined
                            )?.(v);
                            onSettingChange(name, v);
                          }}
                        />
                      </Box>
                    );
                  })}
                </>
              ) : (
                <Typography color="white">
                  No settings available for this widget.
                </Typography>
              )}
            </Box>

            <Box className={widgetSettingsClasses.actions}>
              <Button onClick={handleClose} variant="contained">
                Close
              </Button>
            </Box>
          </Box>
        </StyledPopover>
      </Root>
    );
  },
);

WidgetSettingsPopover.displayName = 'WidgetSettingsPopover';
